import React from 'react';
import './WorkflowArticle.css';

// ─── Shared data ─────────────────────────────────────────────────────────────

const LABEL_COLORS = {
  bug: '#ef4444', feature: '#3b82f6', security: '#e11d48',
  pipeline: '#7c3aed', infra: '#6b7280', enhancement: '#06b6d4',
};

const ALL_ISSUES = [
  { num: 7, title: 'Jobs: "no active roles" on expand', label: 'bug' },
  { num: 6, title: 'Industry classification: reduce "Other"', label: 'pipeline' },
  { num: 5, title: 'Jobs UI: paginate + remove TRENDING badge', label: 'feature' },
  { num: 4, title: 'Research page: surface ferl-lab benchmarks', label: 'feature' },
  { num: 3, title: 'Workflow: vault-backed task list', label: 'infra' },
];

// ─── Mini illustration components ────────────────────────────────────────────

function MiniIssues({ issues, closedFirst }) {
  return (
    <div className="mi-issues">
      <div className="mi-issues-bar">
        <span className="mi-repo">ferl-site</span>
        <span className="mi-count">{issues.length} open</span>
      </div>
      {issues.map((iss, i) => (
        <div key={iss.num} className={`mi-issue-row${closedFirst && i === 0 ? ' mi-issue--closed' : ''}`}>
          <span className="mi-issue-dot" style={{ color: closedFirst && i === 0 ? '#444' : '#3fb950' }}>⊙</span>
          <span className="mi-issue-title">{iss.title}</span>
          <span className="mi-issue-label" style={{ background: LABEL_COLORS[iss.label] + '22', color: LABEL_COLORS[iss.label] }}>
            {iss.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function MiniTaskList({ tasks }) {
  const done = tasks.filter(t => t.status === 'done').length;
  const total = tasks.length;
  return (
    <div className="mi-terminal">
      <div className="mi-terminal-bar">
        <span className="mi-dot mi-dot--r" /><span className="mi-dot mi-dot--y" /><span className="mi-dot mi-dot--g" />
        <span className="mi-terminal-title">claude — tasks</span>
      </div>
      <div className="mi-terminal-body">
        <div className="mi-task-count">{total} tasks ({done} done, {total - done} open)</div>
        {tasks.map((t, i) => (
          <div key={i} className={`mi-task mi-task--${t.status}`}>
            <div className="mi-task-row">
              <span className="mi-task-check">
                {t.status === 'done' ? '✓' : t.status === 'loading' ? '↻' : t.status === 'active' ? '▶' : '◻'}
              </span>
              <span className="mi-task-title">{t.title}</span>
            </div>
            {t.vault !== undefined && (
              <div className="mi-task-vault">
                <span className="mi-vault-arrow">↳</span>
                <span className="mi-vault-tag">vault</span>
                <span className="mi-vault-path">{t.vault || '[pending]'}</span>
              </div>
            )}
            {t.note && <div className="mi-task-note">{t.note}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── Step illustrations ───────────────────────────────────────────────────────

const STEP_ILLUSTRATIONS = {
  github: {
    issues: <MiniIssues issues={ALL_ISSUES} />,
    tasks: <MiniTaskList tasks={[
      { title: 'Jobs UI: TRENDING badge', vault: 'raw/session-exports/2026-05-11-ferl-jobs-ui.md', status: 'pending' },
      { title: 'Research page benchmarks', vault: null, status: 'pending' },
    ]} />,
  },
  sliding: {
    issues: <MiniIssues issues={ALL_ISSUES} />,
    tasks: <MiniTaskList tasks={[
      { title: 'Jobs: fix "no active roles"', status: 'loading' },
      { title: 'Industry enrichment...', status: 'loading' },
      { title: 'Jobs UI: TRENDING badge', status: 'loading' },
      { title: 'Research page benchmarks', status: 'loading' },
      { title: 'Workflow: vault-backed list', status: 'loading' },
    ]} />,
  },
  tasklist: {
    issues: <MiniIssues issues={ALL_ISSUES} />,
    tasks: <MiniTaskList tasks={[
      { title: 'Jobs: fix "no active roles"', vault: null, status: 'pending' },
      { title: 'Industry enrichment', vault: null, status: 'pending' },
      { title: 'Jobs UI: TRENDING badge', vault: null, status: 'pending' },
    ]} />,
  },
  closed: {
    issues: <MiniIssues issues={ALL_ISSUES.slice(1)} />,
    tasks: <MiniTaskList tasks={[
      { title: 'Industry enrichment', vault: null, status: 'pending' },
      { title: 'Jobs UI: TRENDING badge', vault: null, status: 'pending' },
      { title: 'Research page benchmarks', status: 'loading' },
    ]} />,
  },
};

// ─── Tools ───────────────────────────────────────────────────────────────────

const TOOLS = [
  { name: 'Claude Code CLI', role: 'The harness. Implements code, manages the task list, writes vault docs, creates branches, and runs every gh command.', link: 'https://claude.ai/code', tag: 'AI' },
  { name: 'Codex CLI', role: 'Senior engineer review gate via MCP. Every plan goes through Codex before a line of code is written. Catches design flaws, SQL issues, scope problems.', link: 'https://openai.com/codex', tag: 'AI' },
  { name: 'GitHub Issues', role: 'Source of truth for all work. Labels map to vault paths and branch types. Issues persist forever — closed but never deleted.', link: 'https://docs.github.com/en/issues', tag: 'Tracking' },
  { name: 'GitHub PRs', role: 'Formal handoff between implementation and UAT. Each branch maps to one issue. PR links to the issue, deploys to UAT, gets verified, then merges to prod.', link: 'https://docs.github.com/en/pull-requests', tag: 'Tracking' },
  { name: 'Obsidian Vault', role: 'Persistent memory across sessions. Plans, benchmarks, and architecture decisions live here — organized by label type, referenced by path from every task and issue.', link: 'https://obsidian.md', tag: 'Memory' },
  { name: 'UAT → Prod', role: 'The verification gate. Nothing merges to main without passing UAT first. Catches regressions before they become prod fix cycles.', link: null, tag: 'Infra' },
];

// ─── Full loop steps ──────────────────────────────────────────────────────────

const STEPS = [
  {
    icon: '⊙', label: 'GitHub Issues — Source of Truth', color: '#3b82f6', actor: 'You',
    token: { pct: '~15%', label: 'GitHub Issues as memory' },
    illust: 'github',
    desc: 'Every piece of work starts as a labeled GitHub issue. The label — feature, bug, security, pipeline, infra — determines the vault path for its plan and the git branch prefix. Issues persist forever, closed but never deleted.',
  },
  { arrow: true, label: 'Session opens — Claude Code checks task count' },
  {
    icon: '◫', label: 'Sliding Window — Auto Refill to 5', color: '#60a5fa', actor: 'Claude Code',
    token: { pct: '~15%', label: 'Sliding window (5-task cap)' },
    illust: 'sliding',
    desc: 'When task count drops below 5, Claude calls the GitHub API via gh CLI and pulls the next issue by priority (security → bug → pipeline → infra → feature), creating a task with TaskCreate. The list refills itself automatically.',
  },
  { arrow: true, label: 'Task created — title visible in terminal' },
  {
    icon: '◻', label: 'Claude Task List — Always in Terminal', color: '#22c55e', actor: 'Claude Code',
    token: { pct: '~5%', label: 'Vault path on task + issue' },
    illust: 'tasklist',
    desc: 'Up to 5 tasks always visible. Each shows the issue title and the Obsidian vault path of its approved plan. The vault path starts empty — it fills after approval. Sessions pick up exactly where they left off.',
  },
  { arrow: true, label: 'Claude drafts plan: scope, files, risks, verification' },
  {
    icon: '⬡', label: 'Codex Review — Senior Engineer Gate', color: '#a855f7', actor: 'Codex CLI',
    token: { pct: '~10%', label: 'Codex review gate' },
    illust: null,
    desc: 'The plan goes to Codex via MCP before any code is written. Codex reads the actual codebase and checks for route conflicts, SQL errors, and scope problems. Returns approval or specific corrections — nothing proceeds without a pass.',
  },
  { arrow: true, label: 'Codex approves — plan revised if corrections returned' },
  {
    icon: '◉', label: 'You Approve the Plan', color: '#e2e8f0', actor: 'You',
    token: null,
    illust: null,
    desc: 'The Codex-approved plan is presented for final sign-off. The only required human decision in planning. Accept it, change it, or redirect. Nothing goes to the vault until this approval happens.',
  },
  { arrow: true, label: 'Approved — vault doc written, task updated, issue commented' },
  {
    icon: '◈', label: 'Obsidian Vault — Plan Written and Linked', color: '#f59e0b', actor: 'Claude Code',
    token: { pct: '~25%', label: 'Vault-backed plans' },
    illust: null,
    desc: 'Plan written to the vault at a path based on issue label. The task entry is updated with the vault path, and the path is commented on the GitHub issue. Every task and issue now points to the exact plan doc.',
  },
  { arrow: true, label: 'Vault linked — implementation begins' },
  {
    icon: '⌨', label: 'Claude Code — Implementation', color: '#f43f5e', actor: 'Claude Code',
    token: null,
    illust: null,
    desc: 'Claude reads the vault plan and edits only the files it scoped. CLAUDE.md rules enforce lowest effective dose and limited blast radius. Every action is visible in the terminal in real time.',
  },
  { arrow: true, label: 'Code written — branch created from label + issue number' },
  {
    icon: '⌥', label: 'Branch + PR — Paper Trail Connected', color: '#64748b', actor: 'Claude Code',
    token: null,
    illust: null,
    desc: 'Branch named by type and issue — e.g. bug/7-jobs-no-active-roles. PR opened linking to the issue with vault path in the body. Full chain visible: issue → vault → task → implementation → PR.',
  },
  { arrow: true, label: 'PR deployed to UAT — real environment, real database' },
  {
    icon: '✓', label: 'UAT Verify — Real Browser, Real Database', color: '#10b981', actor: 'You',
    token: { pct: '~5%', label: 'UAT gate before prod' },
    illust: null,
    desc: 'Verified in a real browser against a live database — not mocks, not the diff. Catches regressions and integration failures code review misses. Nothing merges to main without passing this gate.',
  },
  { arrow: true, label: 'UAT approved — PR merged — prod deployed' },
  {
    icon: '⊛', label: 'Prod Deploy — Live on ferl.fyi', color: '#34d399', actor: 'Claude Code + You',
    token: null,
    illust: null,
    desc: 'PR merges to main and deploy script runs against EC2. Live site spot-checked post-deploy. Work is complete, permanent, and live.',
  },
  { arrow: true, label: 'Issue closed → vault path commented → task removed → sliding window fires' },
  {
    icon: '⊗', label: 'Issue Closed — Loop Restarts', color: '#6b7280', actor: 'Claude Code',
    token: null,
    illust: 'closed',
    desc: 'Issue closed with vault path commented for permanent traceability. Task removed from the list. Sliding window fires — next issue pulled, new task created, loop restarts from the top.',
  },
];

const EFFICIENCY = [
  { component: 'Vault-backed plans', reduction: '~25%', rationale: 'Plans persist across sessions. Session starts by reading the vault path — no re-explaining decisions, architecture, or what was tried last time.' },
  { component: 'GitHub Issues as memory', reduction: '~15%', rationale: 'The issue body carries the "what and why" permanently. Zero "where were we" overhead at the start of every session.' },
  { component: 'Sliding window (5-task cap)', reduction: '~15%', rationale: 'Scope is always bounded. No mid-session rabbit holes. Every token goes toward the active task.' },
  { component: 'Codex review gate', reduction: '~10%', rationale: 'Bad plans caught before implementation. Rework cycles are the single biggest token sink — this cuts them at the source.' },
  { component: 'Vault path on task + issue', reduction: '~5%', rationale: 'One lookup retrieves full context. No searching conversation history or re-reading old sessions.' },
  { component: 'UAT gate before prod', reduction: '~5%', rationale: 'Catches regressions before they become a second fix cycle on a live system.' },
];

// ─── Main component ───────────────────────────────────────────────────────────

export default function WorkflowArticle() {
  return (
    <article className="wf-article">

      <header className="wf-hero">
        <div className="wf-hero-tag">System · 2026-05-11</div>
        <h1 className="wf-hero-title">How ferl gets built</h1>
        <p className="wf-hero-sub">
          Every feature starts as a GitHub issue and ends as a prod deploy.
          Six tools. One loop. Every decision documented, every token accountable.
        </p>
      </header>

      <section className="wf-section">
        <h2 className="wf-section-title">The problem this solves</h2>
        <p className="wf-body">AI-assisted development has a context problem. Every session starts cold — re-explaining what was decided, what was tried, what the plan was. That overhead isn't just annoying, it's expensive: 20–30% of every session's tokens go toward re-establishing context that should already be known.</p>
        <p className="wf-body">The ferl workflow treats this as an engineering problem. Plans live in a vault. Tasks link to plans. Issues link to tasks. Sessions start with full context, zero re-explanation.</p>
      </section>

      <section className="wf-section">
        <h2 className="wf-section-title">The tools</h2>
        <div className="wf-tools-grid">
          {TOOLS.map(tool => (
            <div key={tool.name} className="wf-tool-card">
              <div className="wf-tool-header">
                <span className={`wf-tool-tag wf-tool-tag--${tool.tag.toLowerCase()}`}>{tool.tag}</span>
                {tool.link
                  ? <a href={tool.link} target="_blank" rel="noopener noreferrer" className="wf-tool-name">{tool.name} ↗</a>
                  : <span className="wf-tool-name">{tool.name}</span>}
              </div>
              <p className="wf-tool-role">{tool.role}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── The loop ─────────────────────────────────────────────────────── */}
      <section className="wf-section">
        <h2 className="wf-section-title">The loop</h2>

        {/* Mobile: clean numbered summary */}
        <div className="wf-loop-mobile">
          {STEPS.filter(s => !s.arrow).map((step, i) => (
            <div key={i} className="wf-mobile-step">
              <div className="wf-mobile-step-icon" style={{ color: step.color, borderColor: step.color + '44' }}>{step.icon}</div>
              <div className="wf-mobile-step-body">
                <div className="wf-mobile-step-label" style={{ color: step.color }}>{step.label}</div>
                <div className="wf-mobile-step-actor">{step.actor}</div>
                <p className="wf-mobile-step-desc">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: full 5-column grid */}
        <div className="wf-loop-section-desktop">
        <div className="wf-loop-headers">
          <span className="lh-token">Token reduction</span>
          <span className="lh-step" style={{ gridColumn: '2 / 4' }}>Step</span>
          <span className="lh-issues">GitHub Issues</span>
          <span className="lh-tasks">Claude Task List</span>
        </div>

        <div className="wf-loop">
          {STEPS.map((step, i) =>
            step.arrow ? (
              <div key={i} className="wf-step-arrow">
                <div className="wf-arrow-line-cell">
                  <div className="wf-arrow-line" />
                </div>
                <span className="wf-arrow-label">{step.label}</span>
              </div>
            ) : (
              <div key={i} className="wf-step">
                {/* Col 1: Token badge */}
                <div className="wf-col-token">
                  {step.token && (
                    <div className="wf-token-badge">
                      <div className="wf-token-pct">{step.token.pct}</div>
                      <div className="wf-token-label">{step.token.label}</div>
                    </div>
                  )}
                </div>

                {/* Col 2: Icon */}
                <div className="wf-step-icon-cell">
                  <div className="wf-step-icon" style={{ color: step.color, borderColor: step.color + '44' }}>
                    {step.icon}
                  </div>
                </div>

                {/* Col 3: Content */}
                <div className="wf-step-content">
                  <div className="wf-step-header">
                    <span className="wf-step-label" style={{ color: step.color }}>{step.label}</span>
                    <span className="wf-step-actor">{step.actor}</span>
                  </div>
                  <p className="wf-step-desc">{step.desc}</p>
                </div>

                {/* Col 4: Issues illustration */}
                <div className="wf-col-issues">
                  {step.illust && STEP_ILLUSTRATIONS[step.illust]?.issues}
                </div>

                {/* Col 5: Tasks illustration */}
                <div className="wf-col-tasks">
                  {step.illust && STEP_ILLUSTRATIONS[step.illust]?.tasks}
                </div>
              </div>
            )
          )}
        </div>
        </div>{/* end wf-loop-section-desktop */}
      </section>

      {/* Token efficiency */}
      <section className="wf-section">
        <h2 className="wf-section-title">Token reduction</h2>
        <p className="wf-body">
          Every component was chosen in part because it reduces token spend — fewer wasted cycles, less re-establishing context, tighter scope. Estimated total reduction vs. unstructured sessions: <strong className="wf-highlight">~40–50%</strong>.
        </p>
        <div className="wf-chart">
          <div className="wf-chart-title">Token reduction per component</div>
          {EFFICIENCY.map((row, i) => {
            const pct = parseFloat(row.reduction.replace(/[^0-9.]/g, '')) || 0;
            return (
              <div key={i} className="wf-chart-row">
                <div className="wf-chart-label">{row.component}</div>
                <div className="wf-chart-bar-track">
                  <div className="wf-chart-bar" style={{ width: `${pct}%` }} />
                </div>
                <div className="wf-chart-pct">{row.reduction}<span className="wf-chart-unit"> token reduction</span></div>
              </div>
            );
          })}
          <div className="wf-chart-total-row">
            <div className="wf-chart-label wf-chart-total-label">Total estimated</div>
            <div className="wf-chart-bar-track">
              <div className="wf-chart-bar wf-chart-bar--total" style={{ width: '45%' }} />
            </div>
            <div className="wf-chart-pct wf-highlight">~40–50%<span className="wf-chart-unit"> token reduction</span></div>
          </div>
        </div>
        <div className="wf-efficiency-table">
          <div className="wf-eff-header">
            <span>Component</span><span>Token Reduction</span><span>Why it works</span>
          </div>
          {EFFICIENCY.map((row, i) => (
            <div key={i} className="wf-eff-row">
              <span className="wf-eff-component">{row.component}</span>
              <span className="wf-eff-reduction">{row.reduction}</span>
              <span className="wf-eff-rationale">{row.rationale}</span>
            </div>
          ))}
          <div className="wf-eff-total">
            <span>Total estimated</span>
            <span className="wf-highlight">~40–50%</span>
            <span>vs. unstructured sessions with no persistent context</span>
          </div>
        </div>
      </section>

      <section className="wf-section wf-closing">
        <p className="wf-body">This workflow is itself tracked in the ferl vault, built using the same loop it describes. Every improvement — new tools, better efficiency numbers, updated rationale — gets logged in the progress feed.</p>
      </section>

    </article>
  );
}
